<template>
  <div class="step-panel" data-testid="payroll-step3-revenue-conciliation">
    <!-- Step Header -->
    <div class="step-header">
      <h2>{{ t('Conciliação de Recebimentos') }}</h2>
    </div>

    <!-- Helper Banner -->
    <div class="helper-banner">
      <i class="pi pi-info-circle"></i>
      <div class="helper-content">
        <strong>{{ t('Conciliação de Receita') }}</strong>
        <ul>
          <li>{{ t('A conciliação de receita garante que os dados de vendas correspondam à receita real') }}</li>
          <li>{{ t('Isso é crítico para o cálculo preciso de comissões sobre receita bruta') }}</li>
          <li>{{ t('Revise cuidadosamente os valores antes de prosseguir') }}</li>
        </ul>
      </div>
    </div>

    <!-- Warning Banner -->
    <div class="warning-banner">
      <i class="pi pi-exclamation-triangle"></i>
      <div class="warning-content">
        <strong>{{ t('Atenção') }}</strong>
        <p>{{ t('Erros na conciliação de receita afetarão todos os cálculos de comissão subsequentes. Certifique-se de que os valores estão corretos.') }}</p>
      </div>
    </div>

    <!-- Important Notes -->
    <div class="important-notes">
      <div class="notes-header">
        <i class="pi pi-info-circle"></i>
        <h3>{{ t('Próximos Passos') }}</h3>
      </div>
      <ul class="notes-list">
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Após concluir a conciliação, prossiga para os adiantamentos se houver regras de comissão executada') }}</span>
        </li>
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Caso contrário, prossiga diretamente para as deduções') }}</span>
        </li>
      </ul>
    </div>

    <p class="step-description">
      {{ t('Revise o status da conciliação de recebimentos e gerencie os recebimentos vinculados') }}
    </p>

    <!-- Conciliation Status Card -->
    <div class="conciliation-card">

      <div class="card-body">
        <!-- Summary Grid -->
        <div class="summary-grid">
          <div class="summary-item">
            <div class="item-label">{{ t('Status') }}</div>
            <div class="item-value">
              <span class="status-indicator" :class="statusClass"></span>
              {{ t(statusText) }}
            </div>
          </div>

          <div class="summary-item">
            <div class="item-label">{{ t('Recebimentos Vinculados') }}</div>
            <div class="item-value">{{ conciliationSummary.count || 0 }}</div>
          </div>

          <div class="summary-item highlight">
            <div class="item-label">{{ t('Valor Conciliado') }}</div>
            <div class="item-value">{{ n(parseFloat(String(conciliationSummary.amount ?? 0)) || 0, 'currency') }}</div>
          </div>
        </div>

        <!-- Action Button -->
        <div class="action-section">
          <Button
            :label="buttonLabel"
            :icon="buttonIcon"
            @click="openConciliationWizard"
            :severity="buttonSeverity"
            size="large"
            class="action-button"
            data-testid="payroll-step3-open-wizard-btn" />
        </div>

        <!-- Info Messages -->
        <div v-if="conciliationSummary.status === 'pending'" class="info-message pending">
          <i class="pi pi-info-circle"></i>
          <span>{{ t('Clique em "Iniciar Conciliação" para começar a vincular recebimentos a este período de folha.') }}</span>
        </div>

        <div v-else-if="conciliationSummary.status === 'in_progress'" class="info-message in-progress">
          <i class="pi pi-sync"></i>
          <span>{{ t('Conciliação em progresso. Você pode continuar adicionando recebimentos ou finalizar o processo.') }}</span>
        </div>

        <div v-else-if="conciliationSummary.status === 'completed'" class="info-message completed">
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Conciliação de recebimentos concluída. Você pode editar a conciliação clicando no botão acima.') }}</span>
        </div>
      </div>
    </div>

    <!-- RevenueConciliationWizard -->
    <!-- Render during edit mode (periodId exists) OR create mode (dates exist from periodFormData) -->
    <RevenueConciliationWizard
      v-if="periodId || (startDate && endDate)"
      :visible="showConciliationWizard"
      :period-id="periodId ?? 0"
      :franchise-id="franchiseId ?? 0"
      :start-date="startDate ?? ''"
      :end-date="endDate ?? ''"
      :user-ids="userIds"
      :payment-rule-ids="paymentRuleIds"
      @update:visible="showConciliationWizard = $event"
      @completed="onConciliationCompleted"
      @closed="onWizardClosed" />

    <!-- Modals are centralized in ModalManager.vue and opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import RevenueConciliationWizard from './RevenueConciliationWizard.vue';
// Modals are centralized in ModalManager.vue and opened via eventBus
import { useRevenueConciliation } from '../../../composables/useRevenueConciliation';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';

type Id = number | string;

interface ConciliationSummary {
  status: 'pending' | 'in_progress' | 'completed' | string;
  amount: number | string;
  count: number;
}

interface PaymentRule {
  id?: Id;
  rule_type?: string;
  user_id?: Id;
  [key: string]: unknown;
}

// Backend uses several legacy field names for the same period dates.
// Kept this loose to preserve the original fallback chain semantics.
interface PeriodObject {
  id?: Id;
  franchise_id?: Id;
  start_date?: string | null;
  StartDate?: string | null;
  period_start_date?: string | null;
  data_inicio?: string | null;
  end_date?: string | null;
  EndDate?: string | null;
  period_end_date?: string | null;
  data_fim?: string | null;
  [key: string]: unknown;
}

interface WizardMachine {
  context: { value: {
    period?: PeriodObject;
    periodFormData?: {
      franchise_id?: Id | null;
      start_date?: string | null;
      end_date?: string | null;
    };
    rules?: PaymentRule[];
  } };
  updateRevenue: (status: string, amount: number | string) => void;
}

// =============================================================================
// INJECT MACHINE - Single Source of Truth
// =============================================================================

const machine = inject<WizardMachine>('wizardMachine')!;

// =============================================================================
// REACTIVE DATA FROM MACHINE
// =============================================================================

/**
 * Get period ID from machine context (converted to Number for prop type safety)
 */
const periodId = computed<number | null>(() => {
  const id = machine.context.value.period?.id;
  return id ? Number(id) : null;
});

/**
 * Get franchise ID from machine context
 */
const franchiseId = computed<number | null>(() => {
  return Number(machine.context.value.period?.franchise_id) ||
         Number(machine.context.value.periodFormData?.franchise_id) ||
         null;
});

/**
 * Get start date from machine context
 * Backend returns start_date (not period_start_date) when creating a period
 */
const startDate = computed<string | null>(() => {
  const period = machine.context.value.period;
  const periodFormData = machine.context.value.periodFormData;

  // Debug: log all period fields to find the correct field name
  log('Step3RevenueConciliationSummary', 'startDate', 'Computing startDate', {
    'period': period,
    'periodFormData': periodFormData,
    'period?.start_date': period?.start_date,
    'period?.period_start_date': period?.period_start_date,
    'period?.StartDate': period?.StartDate,
    'period?.data_inicio': period?.data_inicio,
    'periodFormData?.start_date': periodFormData?.start_date
  });

  // Check all possible date field names
  return period?.start_date ||
         period?.StartDate ||
         period?.period_start_date ||
         period?.data_inicio ||
         periodFormData?.start_date ||
         null;
});

/**
 * Get end date from machine context
 * Backend returns end_date (not period_end_date) when creating a period
 */
const endDate = computed<string | null>(() => {
  const period = machine.context.value.period;
  const periodFormData = machine.context.value.periodFormData;

  log('Step3RevenueConciliationSummary', 'endDate', 'Computing endDate', {
    'period': period,
    'periodFormData': periodFormData,
    'period?.end_date': period?.end_date,
    'period?.EndDate': period?.EndDate,
    'period?.period_end_date': period?.period_end_date,
    'period?.data_fim': period?.data_fim,
    'periodFormData?.end_date': periodFormData?.end_date
  });

  // Check all possible date field names
  return period?.end_date ||
         period?.EndDate ||
         period?.period_end_date ||
         period?.data_fim ||
         periodFormData?.end_date ||
         null;
});

/**
 * Extract unique user IDs from rules
 */
const userIds = computed<Id[]>(() => {
  const rules = machine.context.value.rules || [];
  const ids = [...new Set(rules.map((r) => r.user_id).filter((v): v is Id => Boolean(v)))];
  return ids;
});

/**
 * Extract payment rule IDs from rules
 * Only includes rules related to gross revenue commission (for recebimento filtering)
 */
const paymentRuleIds = computed<Id[]>(() => {
  const rules = machine.context.value.rules || [];
  return rules
    .filter((r) => r.rule_type?.startsWith('commission_over_gross_revenue'))
    .map((r) => r.id)
    .filter((v): v is Id => Boolean(v));
});

// Composables
const { t, n } = useI18n();
const { getRevenueConciliationSummary } = useRevenueConciliation();

// State
const conciliationSummary = ref<ConciliationSummary>({
  status: 'pending',
  amount: 0,
  count: 0
});
const showConciliationWizard = ref<boolean>(false);

const statusClass = computed(() => {
  const status = conciliationSummary.value.status;
  return {
    'pending': status === 'pending',
    'in-progress': status === 'in_progress',
    'completed': status === 'completed'
  };
});

const statusText = computed<string>(() => {
  const status = conciliationSummary.value.status;
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'in_progress':
      return 'In Progress';
    case 'completed':
      return 'Completed';
    default:
      return 'Unknown';
  }
});

const buttonLabel = computed<string>(() => {
  const status = conciliationSummary.value.status;
  switch (status) {
    case 'pending':
      return t('Iniciar Conciliação');
    case 'completed':
      return t('Editar Conciliação');
    default:
      return t('Continuar Conciliação');
  }
});

const buttonIcon = computed<string>(() => {
  const status = conciliationSummary.value.status;
  switch (status) {
    case 'pending':
      return 'pi pi-play';
    case 'completed':
      return 'pi pi-pencil';
    default:
      return 'pi pi-arrow-right';
  }
});

const buttonSeverity = computed<'primary' | 'secondary'>(() => {
  return conciliationSummary.value.status === 'completed' ? 'secondary' : 'primary';
});

// Methods
const loadConciliationSummary = async (): Promise<void> => {
  if (!periodId.value) {
    log('Step3RevenueConciliationSummary', 'loadConciliationSummary', 'No period ID available yet');
    return;
  }

  log('Step3RevenueConciliationSummary', 'loadConciliationSummary', 'Loading summary', {
    periodId: periodId.value
  });

  try {
    const summary = await getRevenueConciliationSummary(periodId.value) as ConciliationSummary;
    conciliationSummary.value = summary;

    // Update machine with conciliation data
    machine.updateRevenue(summary.status, summary.amount);

    log('Step3RevenueConciliationSummary', 'loadConciliationSummary', 'Summary loaded', summary);
  } catch (error) {
    log('Step3RevenueConciliationSummary', 'loadConciliationSummary', 'Error', error);
    eventBus.emit('globalMessage.error', t('Error loading revenue conciliation summary'));
  }
};

const openConciliationWizard = (): void => {
  if (!periodId.value) {
    eventBus.emit('globalMessage.warning', t('Please complete previous steps first'));
    return;
  }

  log('Step3RevenueConciliationSummary', 'openConciliationWizard', 'Opening wizard');
  showConciliationWizard.value = true;
};

const onConciliationCompleted = async (result: unknown): Promise<void> => {
  log('Step3RevenueConciliationSummary', 'onConciliationCompleted', 'Conciliation completed', result);

  // Reload summary to show updated data
  await loadConciliationSummary();

  eventBus.emit('globalMessage.success', t('Revenue conciliation completed successfully'));
};

const onWizardClosed = async (): Promise<void> => {
  log('Step3RevenueConciliationSummary', 'onWizardClosed', 'Wizard closed');

  // Reload summary to reflect any changes made in the wizard
  await loadConciliationSummary();
};


// Watch for period ID changes
watch(() => periodId.value, (newVal) => {
  if (newVal) {
    log('Step3RevenueConciliationSummary', 'watch:periodId', 'Period ID available, loading summary', {
      periodId: newVal
    });
    loadConciliationSummary();
  }
}, { immediate: true });

// Watch for period date changes
watch(() => [startDate.value, endDate.value], () => {
  if (periodId.value) {
    loadConciliationSummary();
  }
});

// Lifecycle
onMounted(() => {
  log('Step3RevenueConciliationSummary', 'mounted', 'Component mounted');

  if (periodId.value) {
    loadConciliationSummary();
  }

  // Listen for conciliation events
  eventBus.on('revenue.conciliation.updated', loadConciliationSummary);
});
</script>

<style scoped>
/* Step Header */
.step-header h2 {
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

/* Helper Banner */
.helper-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--blue-50);
  border-left: 4px solid var(--blue-500);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.helper-banner i {
  color: var(--blue-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.helper-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--blue-700);
}

.helper-content ul {
  margin: 0;
  padding-left: 1.25rem;
}

.helper-content li {
  margin-bottom: 0.25rem;
  color: var(--blue-900);
  font-size: 0.9rem;
}

/* Warning Banner */
.warning-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--orange-50);
  border-left: 4px solid var(--orange-500);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.warning-banner i {
  color: var(--orange-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.warning-content strong {
  display: block;
  margin-bottom: 0.25rem;
  color: var(--orange-700);
}

.warning-content p {
  margin: 0;
  color: var(--orange-900);
  font-size: 0.9rem;
  line-height: 1.5;
}

/* Important Notes */
.important-notes {
  padding: 1.25rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  margin-bottom: 1rem;
}

.notes-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--surface-border);
}

.notes-header i {
  font-size: 1.25rem;
  color: var(--primary-color);
}

.notes-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.notes-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.notes-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  color: var(--text-color);
  line-height: 1.5;
}

.notes-list li i {
  color: var(--primary-color);
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.step-panel {
  width: 100%;
}

.step-panel h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.step-description {
  margin-bottom: 1.5rem;
  color: var(--text-color-secondary);
}

/* Conciliation Card */
.conciliation-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--surface-border);
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, var(--surface-50) 0%, var(--surface-100) 100%);
  border-bottom: 1px solid var(--surface-border);
}

.header-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  transition: all 0.3s ease;
}

.header-icon.pending {
  background: var(--yellow-100);
  color: var(--yellow-700);
}

.header-icon.in-progress {
  background: var(--blue-100);
  color: var(--blue-700);
}

.header-icon.completed {
  background: var(--green-100);
  color: var(--green-700);
}

.header-text {
  flex: 1;
}

.header-text h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.pending {
  background: var(--yellow-100);
  color: var(--yellow-900);
}

.status-badge.in-progress {
  background: var(--blue-100);
  color: var(--blue-900);
}

.status-badge.completed {
  background: var(--green-100);
  color: var(--green-900);
}

.card-body {
  padding: 2rem;
}

/* Summary Grid */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.summary-item {
  padding: 1.25rem;
  background: var(--surface-50);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  transition: all 0.3s ease;
}

.summary-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.summary-item.highlight {
  background: linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%);
  border-color: var(--primary-200);
}

.item-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color-secondary);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.item-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.summary-item.highlight .item-value {
  color: var(--primary-color);
  font-size: 1.75rem;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.status-indicator.pending {
  background: var(--yellow-500);
  animation: pulse-yellow 2s infinite;
}

.status-indicator.in-progress {
  background: var(--blue-500);
  animation: pulse-blue 2s infinite;
}

.status-indicator.completed {
  background: var(--green-500);
}

@keyframes pulse-yellow {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

@keyframes pulse-blue {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Action Section */
.action-section {
  display: flex;
  justify-content: center;
  padding: 1.5rem 0;
  margin-bottom: 1rem;
}

.action-button {
  min-width: 250px;
  font-size: 1.125rem;
  padding: 0.875rem 2rem;
}

/* Info Messages */
.info-message {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  line-height: 1.5;
}

.info-message i {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.info-message.pending {
  background: var(--yellow-50);
  border: 1px solid var(--yellow-200);
  color: var(--yellow-900);
}

.info-message.pending i {
  color: var(--yellow-600);
}

.info-message.in-progress {
  background: var(--blue-50);
  border: 1px solid var(--blue-200);
  color: var(--blue-900);
}

.info-message.in-progress i {
  color: var(--blue-600);
}

.info-message.completed {
  background: var(--green-50);
  border: 1px solid var(--green-200);
  color: var(--green-900);
}

.info-message.completed i {
  color: var(--green-600);
}

/* Responsive */
@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 1.25rem;
  }

  .header-icon {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }

  .card-body {
    padding: 1.5rem;
  }

  .summary-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .item-value {
    font-size: 1.25rem;
  }

  .summary-item.highlight .item-value {
    font-size: 1.5rem;
  }

  .action-button {
    min-width: 100%;
    font-size: 1rem;
  }
}
</style>
